import React, {
  useMemo, useRef, useState, useCallback, useEffect,
  forwardRef, useImperativeHandle,
} from "react";
import { Canvas, useThree, type ThreeEvent } from "@react-three/fiber";
import { OrbitControls, Sphere, Html } from "@react-three/drei";
import * as THREE from "three";
import { getElementColor, type ColorScheme } from "./elementColors";
import { getElementRadius } from "./elementRadii";
import type { Atom, Bond, ParsedStructure } from "./structureParser";
import { calculateBonds, BOND_SCALE } from "./structureParser";
import { BondsMesh, enrichBonds, type RichBond } from "./BondsMesh";
import { buildSupercell, SupercellScene } from "./SupercellScene";

// ── Single Atom Sphere ──────────────────────────────────────────────────

interface AtomSphereProps {
  atom: Atom;
  scale: number;
  onClick: (atom: Atom) => void;
  isSelected: boolean;
  colorScheme: ColorScheme;
}

const AtomSphere: React.FC<AtomSphereProps> = ({
  atom, scale, onClick, isSelected, colorScheme,
}) => {
  const radius = getElementRadius(atom.element) * scale;
  const color = getElementColor(atom.element, colorScheme);
  const emissive = useMemo(
    () => (isSelected ? new THREE.Color("#444444") : new THREE.Color("#000000")),
    [isSelected],
  );

  return (
    <Sphere
      position={[atom.x, atom.y, atom.z]}
      args={[radius, 32, 32]}
      onClick={(e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        onClick(atom);
      }}
    >
      <meshStandardMaterial
        color={color}
        metalness={0.2}
        roughness={0.45}
        emissive={emissive}
        emissiveIntensity={isSelected ? 0.5 : 0}
      />
    </Sphere>
  );
};

// ── Unit Cell Box ───────────────────────────────────────────────────────

const UnitCellBox: React.FC<{ lattice: number[] }> = ({ lattice }) => {
  const geometry = useMemo(() => {
    if (!lattice || lattice.length !== 9) return null;
    const a = new THREE.Vector3(lattice[0], lattice[1], lattice[2]);
    const b = new THREE.Vector3(lattice[3], lattice[4], lattice[5]);
    const c = new THREE.Vector3(lattice[6], lattice[7], lattice[8]);

    const vertices = [
      new THREE.Vector3(0, 0, 0),
      a.clone(),
      b.clone(),
      c.clone(),
      a.clone().add(b),
      a.clone().add(c),
      b.clone().add(c),
      a.clone().add(b).add(c),
    ];

    const edges: [number, number][] = [
      [0, 1], [0, 2], [0, 3],
      [1, 4], [1, 5],
      [2, 4], [2, 6],
      [3, 5], [3, 6],
      [4, 7], [5, 7], [6, 7],
    ];

    const positions: number[] = [];
    edges.forEach(([i, j]) => {
      positions.push(...vertices[i].toArray());
      positions.push(...vertices[j].toArray());
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positions), 3));
    return geo;
  }, [lattice]);

  if (!geometry) return null;

  return (
    <lineSegments geometry={geometry}>
      <lineBasicMaterial color="#00cc44" linewidth={1} />
    </lineSegments>
  );
};

// ── Atom Tooltip ────────────────────────────────────────────────────────

const AtomTooltip: React.FC<{ atom: Atom }> = ({ atom }) => {
  if (!atom) return null;
  return (
    <Html position={[atom.x, atom.y + getElementRadius(atom.element) * 0.4 + 0.3, atom.z]}>
      <div className="atom-tooltip">
        <strong>{atom.label ?? atom.element}</strong>
        <br />
        x: {atom.x.toFixed(3)}
        <br />
        y: {atom.y.toFixed(3)}
        <br />
        z: {atom.z.toFixed(3)}
      </div>
    </Html>
  );
};

// ── Chemical Bonds: see BondsMesh.tsx (shared with SupercellScene) ──────

// ── Camera Controller (auto-fit) ────────────────────────────────────────

const CameraController: React.FC<{
  atoms: Atom[];
  lattice: number[] | null;
  view: string;
  autoRotate?: boolean;
}> = ({ atoms, lattice, view, autoRotate = false }) => {
  const { camera, size } = useThree();
  // size 是 R3F store 对象：数值不变但对象身份会随轮询渲染抖动，
  // 直接放进 effect deps 会导致取景重置、自转被打断。用原始值参与依赖。
  const { width, height } = size;
  const controlsRef = useRef<any>(null);

  // 包围球半径（覆盖任意视角的对角投影——半边长在斜视角下会被低估 ~1.7x）
  const { center, radius } = useMemo(() => {
    if (!atoms.length) return { center: new THREE.Vector3(), radius: 5 };

    const points = atoms.map((a) => new THREE.Vector3(a.x, a.y, a.z));

    if (lattice && lattice.length === 9) {
      const a = new THREE.Vector3(lattice[0], lattice[1], lattice[2]);
      const b = new THREE.Vector3(lattice[3], lattice[4], lattice[5]);
      const c = new THREE.Vector3(lattice[6], lattice[7], lattice[8]);
      points.push(
        new THREE.Vector3(0, 0, 0), a, b, c,
        a.clone().add(b), a.clone().add(c), b.clone().add(c),
        a.clone().add(b).add(c),
      );
    }

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    points.forEach((p) => {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); minZ = Math.min(minZ, p.z);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); maxZ = Math.max(maxZ, p.z);
    });

    let maxR = 0;
    atoms.forEach((a) => { const r = getElementRadius(a.element) * 0.4; if (r > maxR) maxR = r; });
    minX -= maxR; minY -= maxR; minZ -= maxR;
    maxX += maxR; maxY += maxR; maxZ += maxR;

    const center = new THREE.Vector3(
      (minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2,
    );
    // 半径 = 最远点到 center 的距离（原子逐点算，比盒对角更准）
    const radius = Math.max(
      points.reduce((m, p) => Math.max(m, p.distanceTo(center)), 0), 1);
    return { center, radius };
  }, [atoms, lattice]);

  useEffect(() => {
    // 严格取景：包围球同时塞进垂直和水平视锥（取更紧的那个方向）
    const aspect = (width && height) ? width / height : 1;
    const vHalf = (50 * Math.PI) / 360;
    const hHalf = Math.atan(Math.tan(vHalf) * aspect);
    const effHalf = Math.min(vHalf, hHalf);
    const targetRatio = 0.85;
    let dist = radius / (targetRatio * Math.sin(effHalf));
    dist = Math.max(3, Math.min(dist, 300));

    camera.near = Math.max(0.05, dist - radius * 2);
    camera.far = dist + radius * 2 + 20;
    camera.updateProjectionMatrix();

    const pos = camera.position;
    const d = dist;

    switch (view) {
      case "top":
        pos.set(center.x, center.y, center.z + d);
        break;
      case "side":
        pos.set(center.x + d, center.y, center.z);
        break;
      case "front":
        pos.set(center.x, center.y + d, center.z);
        break;
      default:
        pos.set(center.x + d * 0.7, center.y + d * 0.45, center.z + d * 0.55);
        break;
    }
    camera.lookAt(center);
  }, [center, radius, view, camera, width, height]);

  return (
    <OrbitControls
      ref={controlsRef}
      target={[center.x, center.y, center.z]}
      enableZoom
      enablePan
      autoRotate={autoRotate}
      autoRotateSpeed={1.8}
    />
  );
};

// ── Canvas internals: expose gl/scene/camera for PNG export ─────────────

const CanvasInternals: React.FC<{
  onReady: (gl: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) => void;
}> = ({ onReady }) => {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    // 调试钩子：控制台/自动化可直接读相机/渲染器/场景状态
    (window as any).__bcs_cam = camera;
    (window as any).__bcs_gl = gl;
    (window as any).__bcs_scene = scene;
    onReady(gl, scene, camera);
  }, [gl, scene, camera, onReady]);
  return null;
};

// ── Main CrystalViewer ──────────────────────────────────────────────────

export interface CrystalViewerHandle {
  /** Render a fresh frame and trigger a PNG download. */
  exportPNG: (filename?: string) => void;
}

export interface CrystalViewerProps {
  structure: ParsedStructure | null;
  atomRadiusScale?: number;
  view?: string;
  colorScheme?: ColorScheme;
  className?: string;
  autoRotate?: boolean;
  /** 超胞展开动画（1×1×1 → 2×1×1 → 2×2×1 → 2×2×2 循环）。大结构自动跳过。 */
  supercellAnimation?: boolean;
}

/** 超胞动画规模上限：原子数 × n³ 超过此值不启用（与 system0 kwj 版一致）。 */
const SUPERCELL_ATOM_BUDGET = 800;

const CrystalViewer = forwardRef<CrystalViewerHandle, CrystalViewerProps>((
  {
    structure,
    atomRadiusScale = 0.4,
    view = "isometric",
    colorScheme = "jmol",
    className,
    autoRotate = false,
    supercellAnimation = true,
  },
  ref,
) => {
  const [selectedAtom, setSelectedAtom] = useState<Atom | null>(null);

  // Hold the latest gl/scene/camera from inside the Canvas so exportPNG can
  // force a render and call toDataURL.
  const internalsRef = useRef<{
    gl: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.Camera;
  } | null>(null);

  const handleInternalsReady = useCallback(
    (gl: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) => {
      internalsRef.current = { gl, scene, camera };
    },
    [],
  );

  useImperativeHandle(ref, () => ({
    exportPNG: (filename = "structure.png") => {
      const ctx = internalsRef.current;
      if (!ctx) return;
      const { gl, scene, camera } = ctx;
      // Force a fresh render so the drawing buffer is populated (browsers may
      // clear it between frames when preserveDrawingBuffer is true but the
      // scene hasn't changed).
      gl.render(scene, camera);
      const dataUrl = gl.domElement.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
  }), []);

  const handleAtomClick = useCallback((atom: Atom) => {
    setSelectedAtom((prev) => (prev === atom ? null : atom));
  }, []);

  const handleMissed = useCallback(() => {
    setSelectedAtom(null);
  }, []);

  const atoms = structure?.atoms ?? [];
  const lattice = structure?.lattice ?? null;

  const enrichedBonds = useMemo(() => {
    if (!structure || !atoms.length) return [] as RichBond[];
    const raw = structure.bonds || calculateBonds(atoms, lattice, BOND_SCALE);
    return enrichBonds(raw, atoms);
  }, [structure, atoms, lattice]);

  // 超胞展开动画：仅周期性小结构启用（原子数 × n³ ≤ 800，n=2）。
  const supercellSpec = useMemo(() => {
    if (!supercellAnimation || !lattice || !atoms.length) return null;
    if (atoms.length * 8 > SUPERCELL_ATOM_BUDGET) return null;
    return buildSupercell(atoms, lattice, enrichedBonds, 2);
  }, [supercellAnimation, atoms, lattice, enrichedBonds]);

  // 超胞模式下相机按满超胞取景（与 system0 kwj 版一致）。
  const camAtoms = supercellSpec ? supercellSpec.allAtoms : atoms;
  const camLattice = supercellSpec ? supercellSpec.supercellLattice : lattice;

  if (!structure || !structure.atoms.length) {
    return (
      <div className={`crystal-viewer empty ${className ?? ""}`}>
        <p>No structure loaded. Upload a CIF/POSCAR/XYZ file or paste content.</p>
      </div>
    );
  }

  return (
    <div className={`crystal-viewer ${className ?? ""}`}>
      <Canvas
        camera={{ position: [0, 0, 10], fov: 50 }}
        gl={{ antialias: true, preserveDrawingBuffer: true }}
        onPointerMissed={handleMissed}
      >
        <color attach="background" args={["#1a1a2e"]} />
        <hemisphereLight args={["#b8c8ff", "#2a2018", 0.55]} />
        <ambientLight intensity={0.35} />
        <pointLight position={[10, 10, 10]} intensity={0.9} />
        <directionalLight position={[5, 5, 5]} intensity={0.6} />

        <CanvasInternals onReady={handleInternalsReady} />
        <CameraController atoms={camAtoms} lattice={camLattice} view={view}
                          autoRotate={autoRotate} />

        {supercellSpec ? (
          <SupercellScene
            spec={supercellSpec}
            atomRadiusScale={atomRadiusScale}
            colorScheme={colorScheme}
          />
        ) : (
          <>
            <BondsMesh bonds={enrichedBonds} colorScheme={colorScheme} />

            {atoms.map((atom, idx) => (
              <AtomSphere
                key={idx}
                atom={atom}
                scale={atomRadiusScale}
                onClick={handleAtomClick}
                isSelected={selectedAtom === atom}
                colorScheme={colorScheme}
              />
            ))}

            {lattice && <UnitCellBox lattice={lattice} />}
          </>
        )}
        {selectedAtom && <AtomTooltip atom={selectedAtom} />}
      </Canvas>
    </div>
  );
});

CrystalViewer.displayName = "CrystalViewer";

export default CrystalViewer;
